import { useContext } from "react";

import { useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { CoachingHeader } from "@/components/pieces/coaching/CoachingHeader";
import { ResponseContext } from "@/components/ResponseContext";
import {
  CARD_CLASSES,
  INNER_BORDER_COLOR,
  LIGHTEST_BACKGROUND,
  SIDE_NAV_WIDTH,
  SITE_NAME,
  STANDARD_APP_MAX_WIDTH,
} from "@/css/constants";
import { useProductDetailQuery } from "@/graphql";

import { AcceptingNewClients } from "./AcceptingNewClients";
import { BookingRequestToggle } from "./BookingRequestToggle";
import { ClientSettings } from "./ClientSettings";
import { PriceSettings } from "./PriceSettings";
import { ProductInformation } from "./ProductInformation";
import { ToggleProductEnabled } from "./ToggleProductEnabled";
import { WorkspaceSetting } from "./WorkspaceSetting";

const BORDER_STYLES = { borderBottom: `1px solid ${INNER_BORDER_COLOR}` };

export function AuthServiceDetail() {
  const { productId } = useParams();
  const { res } = useContext(ResponseContext);
  const { data, loading } = useProductDetailQuery({
    variables: {
      productId: productId || "",
      skipUserContext: true, // Skip user context to get correct coach product price
    },
    skip: !productId,
  });

  if (!loading && !data) {
    if (res) {
      res.status(404);
    }
    return <NotFound />;
  }

  const product = data?.productById?.product;
  const coachProduct = data?.productById?.coachProduct;

  const { name = "Coach Product", slug, description } = product || {};

  return (
    <>
      <SEOHead
        title={`${name} - ${SITE_NAME}`}
        description={description}
        canonicalPath={`/services/${slug}`}
      />
      <Nav />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        <CoachingHeader selectedTab="services" />
        <Box pb={6} bg={LIGHTEST_BACKGROUND}>
          <Box
            className="container"
            maxWidth={STANDARD_APP_MAX_WIDTH}
            m="0 auto"
            px={[3, 24]}
          >
            <Box width={1} mt={4}>
              <Box
                className={CARD_CLASSES}
                backgroundColor="#FFF"
                py={[3, 3, 3, 24]}
                px={[3, 3, 3, 4]}
              >
                {product && (
                  <Box pb={4} css={BORDER_STYLES}>
                    <ProductInformation product={product} loading={loading} />
                  </Box>
                )}
                {coachProduct && product && (
                  <Box pb={4} pt={4} css={BORDER_STYLES}>
                    <ToggleProductEnabled coachProduct={coachProduct} />
                    <AcceptingNewClients coachProduct={coachProduct} />
                    {product.enableBookingRequest && (
                      <BookingRequestToggle coachProduct={coachProduct} />
                    )}
                  </Box>
                )}
              </Box>
              <Box pt={4}>
                <PriceSettings
                  coachProduct={coachProduct}
                  product={product}
                  loading={loading}
                />
              </Box>
              <Box pt={4}>
                <WorkspaceSetting coachProduct={coachProduct} />
              </Box>
              <Box pt={4}>
                <ClientSettings coachProduct={coachProduct} loading={loading} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export function ServiceDetailPage() {
  return (
    <Authenticate requireCoach={true} maxWidth="100%" layout="dashboard">
      <AuthServiceDetail />
    </Authenticate>
  );
}
